/**
 * videoSessionsAnalyzer — bridges the offline video analyser with the
 * Sessions (live workout) data model so the two features show the SAME
 * cards, the SAME joint-angle rows, and the SAME sub-scores.
 *
 * Why this exists:
 *   Live Sessions read an LLM-generated exercise definition (form_checkpoints,
 *   primary_motion, ideal_range, phase) and feed it through `usePoseDetection3D`
 *   to produce per-rep angles. The post-workout card shows each checkpoint
 *   with its measured value, ideal range, status, and view tag.
 *
 *   The Video Analyser was emitting its own disconnected metric vocabulary
 *   (e.g. "knee_flexion_peak_deg") with no link to the same definition.
 *   This module measures the SAME checkpoints from uploaded-video frames so
 *   the report renders with Sessions' card grid verbatim.
 *
 * Input frames have 3D world landmarks (camera-angle independent) plus 2D
 * landmarks for drawing. We use 3D for angle math wherever possible.
 */

// 3D angle at point B formed by A->B->C, in degrees.
const angle3D = (A, B, C) => {
  if (!A || !B || !C) return null;
  const v1x = A.x - B.x, v1y = A.y - B.y, v1z = (A.z || 0) - (B.z || 0);
  const v2x = C.x - B.x, v2y = C.y - B.y, v2z = (C.z || 0) - (B.z || 0);
  const m1 = Math.hypot(v1x, v1y, v1z);
  const m2 = Math.hypot(v2x, v2y, v2z);
  if (m1 < 1e-6 || m2 < 1e-6) return null;
  const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y + v1z * v2z) / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
};

// Compute the angle at a landmark triplet for one frame, picking 3D first
// then falling back to 2D landmarks if 3D is missing.
const angleForFrame = (frame, triplet) => {
  if (!triplet || triplet.length !== 3) return null;
  const lm3 = frame?.landmarks3D;
  if (lm3 && lm3[triplet[0]] && lm3[triplet[1]] && lm3[triplet[2]]) {
    return angle3D(lm3[triplet[0]], lm3[triplet[1]], lm3[triplet[2]]);
  }
  const lm2 = frame?.landmarks2D;
  if (lm2 && lm2[triplet[0]] && lm2[triplet[1]] && lm2[triplet[2]]) {
    return angle3D(lm2[triplet[0]], lm2[triplet[1]], lm2[triplet[2]]);
  }
  return null;
};

const safeMin = (a) => (a.length ? Math.min(...a) : null);
const safeMax = (a) => (a.length ? Math.max(...a) : null);
const safeAvg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

// Robust trimmed median — same idea as backend's robust_pick_angle.
const robustPick = (values, kind = 'avg') => {
  if (!values || values.length === 0) return null;
  let vs = [...values].sort((x, y) => x - y);
  if (vs.length >= 5) {
    const trim = Math.max(1, Math.floor(vs.length / 5));
    if (vs.length > 2 * trim) vs = vs.slice(trim, vs.length - trim);
  }
  if (kind === 'avg') return vs.reduce((x, y) => x + y, 0) / vs.length;
  const mid = Math.floor(vs.length / 2);
  return vs.length % 2 ? vs[mid] : (vs[mid - 1] + vs[mid]) / 2;
};

// Decide if this checkpoint can be measured from the user's chosen view.
// Mirrors Sessions' view eligibility: front-only metrics (symmetry, valgus,
// tilt) cannot be reliably measured from a side video and are dropped.
const checkpointVisibleFromView = (checkpoint, view) => {
  const cv = (checkpoint.view || 'both').toLowerCase();
  const v = (view || 'side').toLowerCase();
  if (cv === 'both' || cv === 'any') return true;
  return cv === v;
};

// Soft proximity score: in-range → 100, decays outside (matches backend).
const proximityScore = (value, lo, hi) => {
  if (value == null || lo == null || hi == null) return 60;
  if (lo <= value && value <= hi) return 100;
  const width = Math.max(1, hi - lo);
  const dist = value < lo ? lo - value : value - hi;
  return Math.max(10, Math.round(100 - 50 * (dist / width)));
};

const statusFor = (value, lo, hi) => {
  if (lo == null || hi == null || value == null) return 'neutral';
  if (lo <= value && value <= hi) return 'good';
  const slack = Math.max((hi - lo) * 0.2, 5);
  if (lo - slack <= value && value <= hi + slack) return 'warning';
  return 'off';
};

// Find the frame indices that fall inside each rep window. We treat a rep
// as the [prevPeak..nextPeak] interval around each peak timestamp.
const buildRepWindows = (frames, repTimestamps) => {
  if (!frames.length || !repTimestamps || repTimestamps.length === 0) {
    return frames.length ? [[0, frames.length - 1]] : [];
  }
  const peakIdxs = repTimestamps.map(t => {
    let best = 0, bestDt = Infinity;
    for (let i = 0; i < frames.length; i++) {
      const dt = Math.abs((frames[i].t ?? 0) - t);
      if (dt < bestDt) { bestDt = dt; best = i; }
    }
    return best;
  });
  // Cut windows at midpoints between consecutive peaks.
  const windows = [];
  for (let i = 0; i < peakIdxs.length; i++) {
    const peak = peakIdxs[i];
    const start = i === 0
      ? 0
      : Math.floor((peakIdxs[i - 1] + peak) / 2);
    const end = i === peakIdxs.length - 1
      ? frames.length - 1
      : Math.floor((peak + peakIdxs[i + 1]) / 2);
    windows.push([start, end]);
  }
  return windows;
};

/**
 * buildSessionsAnalysis — produce the Sessions-shape report data block from
 * an offline-processed video.
 *
 * @param {object} args
 * @param {Array}  args.frames           per-frame {t, landmarks2D, landmarks3D}
 * @param {object} args.definition       exercise_definition from /api/exercise-definition/{name}
 * @param {object} args.metrics          legacy metrics from analyzeMovement(): rep_timestamps, rep_count, view_detected
 * @param {string} args.exerciseName     human-readable label
 *
 * @returns {object}  joint_angles[], views_captured, technique, balance, pace, score, rep_count, tempo_seconds
 */
export const buildSessionsAnalysis = ({ frames, definition, metrics, exerciseName }) => {
  const view = (metrics?.view_detected || definition?.best_view || 'side').toLowerCase();
  const repTimestamps = Array.isArray(metrics?.rep_timestamps) ? metrics.rep_timestamps : [];
  const windows = buildRepWindows(frames, repTimestamps);
  const checkpoints = Array.isArray(definition?.form_checkpoints) ? definition.form_checkpoints : [];

  const jointAngles = [];
  const seenLabels = new Set();

  for (const cp of checkpoints) {
    if (!cp || !cp.label) continue;
    if (seenLabels.has(cp.label)) continue;
    if (!checkpointVisibleFromView(cp, view)) continue;

    const triplets = [cp.landmark_triplet, cp.landmark_triplet_alt].filter(t => Array.isArray(t) && t.length === 3);
    if (!triplets.length) continue;

    const ir = cp.ideal_range || [];
    if (ir.length < 2) continue;
    const phase = cp.phase || 'throughout';

    // Aggregate values across all reps for this checkpoint.
    const repValues = [];
    for (const [s, e] of windows) {
      const valsInWindow = [];
      for (let i = s; i <= e; i++) {
        // Try every available triplet (L then R) and average them per frame
        // so a single missing side doesn't drop the rep entirely.
        const frameVals = [];
        for (const t of triplets) {
          const v = angleForFrame(frames[i], t);
          if (v != null && Number.isFinite(v)) frameVals.push(v);
        }
        if (frameVals.length) valsInWindow.push(frameVals.reduce((x, y) => x + y) / frameVals.length);
      }
      if (!valsInWindow.length) continue;
      let v;
      if (phase === 'at_bottom') v = safeMin(valsInWindow);
      else if (phase === 'at_top') v = safeMax(valsInWindow);
      else v = robustPick(valsInWindow, 'avg');
      if (v != null && Number.isFinite(v)) repValues.push(v);
    }

    if (!repValues.length) continue;
    const picked = robustPick(repValues, phase === 'at_bottom' ? 'min' : phase === 'at_top' ? 'max' : 'avg');
    if (picked == null) continue;
    const value = Math.round(picked * 10) / 10;
    const lo = ir[0], hi = ir[ir.length - 1];
    jointAngles.push({
      label: cp.label,
      value,
      phase,
      ideal_min: lo,
      ideal_max: hi,
      status: statusFor(value, lo, hi),
      view,
      sample_count: repValues.length,
      tip_good: cp.tip_good || null,
      tip_bad: cp.tip_bad || null,
    });
    seenLabels.add(cp.label);
  }

  // Sub-scores — Technique from joint angles; Balance only if measurable from
  // the captured view; Pace from rep tempo.
  // STRICT SCORING: a single out-of-range joint hard-caps Technique at 70, a
  // single "close" joint caps it at 88. This prevents misleading 99/100
  // "Excellent" scores when one joint is clearly bad — matches what a real
  // coach would call out (you don't get a 99 on a squat with a 75° forward lean).
  let technique = null;
  if (jointAngles.length) {
    const raw = jointAngles.reduce((s, a) => s + proximityScore(a.value, a.ideal_min, a.ideal_max), 0) / jointAngles.length;
    const hasOff = jointAngles.some(a => a.status === 'off');
    const hasWarning = jointAngles.some(a => a.status === 'warning');
    let cap = 100;
    if (hasOff) cap = 70;
    else if (hasWarning) cap = 88;
    technique = Math.round(Math.min(raw, cap) * 10) / 10;
  }

  // Balance is a front-view-only metric. Disable for side/angled views.
  let balance = null;
  if (view === 'front') {
    // Use the existing symmetry chip values from the legacy analyzer.
    const sym = metrics?.asymmetry_max_pct ?? metrics?.worst_asymmetry_pct;
    if (typeof sym === 'number') {
      // sym is the L/R angle difference in degrees / asymmetry percent.
      // Convert to a 0-100 score: 0° diff = 100, 15° diff = 0.
      balance = Math.max(0, Math.round(100 - sym * 6.7));
    }
  }

  const tempo = typeof metrics?.rep_tempo_avg_sec === 'number' ? metrics.rep_tempo_avg_sec : null;
  let pace = null;
  if (tempo != null && tempo > 0) {
    if (tempo < 1.5) pace = 85;
    else if (tempo < 2.5) pace = 100;
    else if (tempo < 4.0) pace = 100;
    else if (tempo < 6.0) pace = 60;
    else pace = 50;
  }

  let score = null;
  if (technique != null) {
    const bal = balance != null ? balance : technique;
    const pc = pace != null ? pace : technique;
    score = Math.round(technique * 0.5 + bal * 0.3 + pc * 0.2);
  }

  const viewsCaptured = jointAngles.length ? { [view]: metrics?.rep_count || 1 } : {};

  return {
    exercise: exerciseName,
    joint_angles: jointAngles,
    views_captured: viewsCaptured,
    technique,
    balance,
    pace,
    score,
    rep_count: metrics?.rep_count || 0,
    tempo_seconds: tempo,
    view_detected: view,
  };
};
